/**
 * Hook to check if the current user is an admin (their email is in the admin whitelist).
 * Reads the email from loggedInUser in localStorage; if empty, falls back to Firebase
 * Auth current user so the check still works when localStorage wasn't set (e.g. WebView, new tab).
 */
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from './firebaseConfig';
import { isAdminEmail } from '../../admin/utils/adminWhitelist';

const auth = getAuth(firebaseApp);

function getStoredEmail(stored: string | null): string | null {
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as { email?: string };
    const email = parsed?.email;
    return email && String(email).trim() !== '' ? String(email).trim() : null;
  } catch {
    return null;
  }
}

export function useIsAdmin(): boolean {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('loggedInUser');
    const storedEmail = getStoredEmail(stored);
    if (storedEmail) {
      setEmail(storedEmail);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setEmail(user?.email?.trim() || null);
    });
    return () => unsubscribe();
  }, []);

  return isAdminEmail(email);
}
